import * as Yup from 'yup';
import * as React from 'react';
import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { Field, Formik } from 'formik';
import { Grid, Skeleton } from '@mui/material';

import {
	CerebroPageContainer,
	CerebroBreadcrumb,
	CerebroHeading,
	CerebroText,
	CerebroLabel,
	CerebroPrimaryButton,
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroYesNo,
	CerebroMaskedInput,
	CerebroForm,
} from '../shared/components';
import { useApi } from '../shared/context';
import { FormikUtils, NumberUtils } from '../shared/utils';
import { Deal, FinancialStatementType, FinancialStatementTypeMap, UpdateDealDto } from '../shared/models';
import { QueryKeys, useDealQuery, useNavigateToNextPage } from '../shared/hooks';
import { CerebroApiEndpoints } from '../shared/utils/api';
import { MaxCurrencyLength } from '../shared/utils/platform';
import { getApiResponseData } from '../shared/utils/axios';

export const AcquisitionBasicInfo: React.FC = () => {
	const snackbar = useSnackbar();

	const api = useApi();

	const navigateToNextPage = useNavigateToNextPage();

	const { guid } = useParams<'guid'>();

	const queryClient = useQueryClient();

	const dealQuery = useDealQuery(guid);

	const dealMutation = useMutation(
		async ({ values }: DealMutationVariables) => {
			const dealPatch: UpdateDealDto = {
				...values,
				acquisitionPurchaseOrderAverageMonthlyCost:
					NumberUtils.ParseDecimal(values.acquisitionPurchaseOrderAverageMonthlyCost) ?? null,
			};

			const { data, error } = await getApiResponseData<Deal>(
				api.patch(CerebroApiEndpoints.Deals.Root(guid), dealPatch)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: (deal: Deal | null) => {
				queryClient.setQueryData(QueryKeys.Deal.Root(guid), deal);
				snackbar.enqueueSnackbar('Progress Saved.', { variant: 'success' });
				navigateToNextPage();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroPageContainer>
			<CerebroBreadcrumb>Financials &gt; Acquisition &gt; Basic Info</CerebroBreadcrumb>
			<CerebroHeading>Tell us about the acquisition&#39;s finances</CerebroHeading>
			<CerebroText sx={{ marginTop: 3, marginBottom: 6 }}>
				Financials, such as income statement and balance sheet metrics, will be used to calculate your debt
				capacity, credit strength, and available loan options.
			</CerebroText>

			<Formik
				validationSchema={AcquisitionBasicInfoSchema}
				enableReinitialize
				onSubmit={FormikUtils.MutateOnSubmit(dealMutation.mutate)}
				initialValues={{
					acquisitionCurrency: dealQuery.data?.acquisitionCurrency ?? 'USD',
					acquisitionFinancialStatementType: dealQuery.data?.acquisitionFinancialStatementType ?? 'None',
					acquisitionCustomerPurchaseOrders: dealQuery.data?.acquisitionCustomerPurchaseOrders ?? null,
					acquisitionPurchaseOrderAverageMonthlyCost: (
						dealQuery.data?.acquisitionPurchaseOrderAverageMonthlyCost ?? ''
					).toString(),
				}}
			>
				{({ values, isSubmitting, errors, touched, handleSubmit }) => (
					<CerebroForm onSubmit={handleSubmit}>
						<CerebroLabel htmlFor="acquisitionFinancialStatementType" sx={{ marginTop: 6 }}>
							What type of financial statements does the company have?
						</CerebroLabel>
						<Grid container spacing={3}>
							<Grid item xs={6}>
								{dealQuery.isSuccess ? (
									<Field
										id="acquisitionFinancialStatementType"
										as={CerebroDropdown}
										name="acquisitionFinancialStatementType"
										disabled={isSubmitting}
									>
										{Object.entries(FinancialStatementTypeMap).map(([value, description]) => (
											<CerebroDropdownMenuItem key={value} value={value}>
												{description}
											</CerebroDropdownMenuItem>
										))}
									</Field>
								) : (
									<Skeleton height={60} />
								)}
							</Grid>
						</Grid>

						<CerebroLabel htmlFor="acquisitionCustomerPurchaseOrders" sx={{ marginTop: 6 }}>
							Do your customers execute purchase orders?
						</CerebroLabel>
						<Field
							id="acquisitionCustomerPurchaseOrders"
							as={CerebroYesNo}
							name="acquisitionCustomerPurchaseOrders"
							disabled={isSubmitting}
						/>

						{values.acquisitionCustomerPurchaseOrders && (
							<Grid container spacing={3}>
								<Grid item xs={6}>
									<CerebroLabel
										htmlFor="acquisitionPurchaseOrderAverageMonthlyCost"
										sx={{ marginTop: 6 }}
									>
										Average aggregate monthly cost to fulfill POs?
									</CerebroLabel>
									<Field
										formatas="currency"
										as={CerebroMaskedInput}
										maxLength={MaxCurrencyLength}
										id="acquisitionPurchaseOrderAverageMonthlyCost"
										name="acquisitionPurchaseOrderAverageMonthlyCost"
										disabled={isSubmitting}
									/>
								</Grid>
							</Grid>
						)}

						<CerebroPrimaryButton type="submit" loading={isSubmitting} sx={{ marginTop: 6 }}>
							Save &amp; Continue
						</CerebroPrimaryButton>
					</CerebroForm>
				)}
			</Formik>
		</CerebroPageContainer>
	);
};

const AcquisitionBasicInfoSchema = Yup.object().shape({
	acquisitionCurrency: Yup.string(),
	acquisitionFinancialStatementType: Yup.string(),
});

type DealMutationVariables = FormikUtils.MutateOnSubmitVariables<AcquisitionBasicInfoFormValues>;
interface AcquisitionBasicInfoFormValues {
	acquisitionCurrency: string;
	acquisitionFinancialStatementType: FinancialStatementType;
	acquisitionCustomerPurchaseOrders: boolean | null;
	acquisitionPurchaseOrderAverageMonthlyCost: string;
}
